import api from './api';
import { uuid } from '../utils/format';
import { itemUnitPrice } from '../utils/restaurantModifiers';

function toAmount(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
}

function positiveInt(value) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function normalizeKey(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
}

function buildItem(item) {
  const idProducto = positiveInt(item?.producto?.id ?? item?.id_producto ?? item?.id);
  const cantidad = Number(item?.cantidad || 0);
  const precioBruto = itemUnitPrice(item);
  const impuestoPct = Number(item?.producto?.impuesto_porcentaje ?? item?.impuesto_porcentaje ?? 0) || 0;

  if (!idProducto || !Number.isFinite(cantidad) || cantidad <= 0) {
    throw new Error('Hay un producto inválido en el pedido.');
  }

  const totalBruto = toAmount(precioBruto * cantidad);
  const divisor = 1 + impuestoPct / 100;
  const totalNeto = divisor > 0 ? toAmount(totalBruto / divisor) : totalBruto;
  const totalIva = toAmount(totalBruto - totalNeto);
  const precioNeto = cantidad > 0 ? toAmount(totalNeto / cantidad) : 0;

  return {
    id_producto: idProducto,
    cantidad,
    precio_neto: precioNeto,
    impuesto_pct: impuestoPct,
    precio_bruto: toAmount(precioBruto),
    total_neto: totalNeto,
    total_iva: totalIva,
    total_bruto: totalBruto,
    sku: item?.producto?.sku || null,
    nombre: item?.producto?.nombre || null,
    tipo_item: item?.producto?.tipo_item || 'PRODUCTO',
    afecta_inventario: item?.producto?.controla_stock !== false,
  };
}

function buildPayment(payment) {
  const relationId = positiveInt(payment?.payment_relation_id ?? payment?.relationId ?? payment?.relation_id);
  const methodId = positiveInt(payment?.payment_method_id ?? payment?.methodId ?? payment?.method_id ?? payment?.metodopago_id);
  const conditionId = positiveInt(payment?.payment_condition_id ?? payment?.conditionId ?? payment?.condition_id ?? payment?.condicionpago_id);
  const methodKey = normalizeKey(payment?.payment_method_key ?? payment?.key ?? payment?.clave);
  const conditionKey = normalizeKey(payment?.payment_condition_key ?? payment?.conditionKey ?? payment?.condicionpago_clave);
  const amountApplied = toAmount(payment?.amount_applied);

  if (amountApplied <= 0) throw new Error('Monto de pago inválido.');
  if (!relationId && !methodId && !methodKey) throw new Error('Medio de pago inválido.');

  const out = {
    amount_applied: amountApplied,
    amount_paid: toAmount(payment?.amount_paid ?? amountApplied),
    amount_received: toAmount(payment?.amount_received ?? payment?.amount_paid ?? amountApplied),
    change_given: toAmount(payment?.change_given ?? 0),
    cash_rounding: toAmount(payment?.cash_rounding ?? 0),
  };

  if (relationId) out.payment_relation_id = relationId;
  if (methodId) out.payment_method_id = methodId;
  if (conditionId) out.payment_condition_id = conditionId;
  if (methodKey) out.payment_method_key = methodKey;
  if (conditionKey) out.payment_condition_key = conditionKey;
  if (payment?.label || payment?.nombre) out.payment_method_name = payment.label || payment.nombre;
  if (payment?.reference) {
    out.reference = String(payment.reference);
    out.referencia = String(payment.reference);
    out.voucher_number = String(payment.reference);
  }

  return out;
}

export function buildVentaPosPayload({ order, payments, documento, cliente, cajaId, clientSaleId }) {
  const items = (order?.items || []).map(buildItem);
  const paymentRows = (payments || []).map(buildPayment);

  if (!items.length) throw new Error('El pedido no tiene productos.');
  if (!paymentRows.length) throw new Error('No hay pagos informados.');

  const totalBruto = toAmount(items.reduce((sum, item) => sum + item.total_bruto, 0));
  const totalNeto = toAmount(items.reduce((sum, item) => sum + item.total_neto, 0));
  const totalIva = toAmount(items.reduce((sum, item) => sum + item.total_iva, 0));
  const applied = toAmount(paymentRows.reduce((sum, item) => sum + item.amount_applied, 0));
  const totalCLP = Math.round(totalBruto);

  const payload = {
    client_sale_id: clientSaleId || uuid(),
    document_type: documento === 'factura' ? 'factura' : 'boleta',
    pdf_format: process.env.REACT_APP_DTE_PDF_FORMAT || '80mm',
    currency: 'CLP',
    status: Math.abs(applied - totalCLP) <= 0.009 ? 'completed' : 'pending',
    remaining_balance: Math.max(0, toAmount(totalCLP - applied)),
    total_amount: totalCLP,
    total_bruto: totalCLP,
    total_neto: totalNeto,
    tax: totalIva,
    total_iva: totalIva,
    items,
    payments: paymentRows,
  };

  const customerId = positiveInt(cliente?.id);
  const posCajaId = positiveInt(cajaId);
  if (customerId) payload.customer_id = customerId;
  if (posCajaId) payload.pos_caja_id = posCajaId;

  return payload;
}

export async function crearVentaPos(payload) {
  const { data } = await api.post('/ventas_pos', payload, { timeout: 90000 });
  return data;
}

function pdfEndpoint(result, documento) {
  if (result?.dte?.pdf_url) return result.dte.pdf_url;
  if (result?.dte?.pdf_endpoint) return result.dte.pdf_endpoint;
  const id = positiveInt(result?.venta?.id_venta);
  if (!id) return null;
  return documento === 'factura'
    ? `/facturacion_dte33/${id}/pdf`
    : `/facturacion_dte39/${id}/pdf`;
}

export async function imprimirDte(result, documento = 'boleta') {
  const endpoint = pdfEndpoint(result, documento);
  if (!endpoint) throw new Error('No se encontró el PDF del DTE.');
  const format = process.env.REACT_APP_DTE_PDF_FORMAT || '80mm';
  const separator = endpoint.includes('?') ? '&' : '?';
  const { data } = await api.get(`${endpoint}${separator}formato=${encodeURIComponent(format)}`, {
    responseType: 'blob',
    timeout: 60000,
  });

  const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.src = url;
  iframe.onload = () => {
    window.setTimeout(() => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      window.setTimeout(() => {
        iframe.remove();
        window.URL.revokeObjectURL(url);
      }, 30000);
    }, 250);
  };
  document.body.appendChild(iframe);
}
